import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../services/api.service';
import { extractData, extractMessage, extractErrorMessage, ApiResponse } from '../../../helpers/api-response.helper';

@Component({
  selector: 'app-leads',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './leads.component.html',
  styleUrls: ['./leads.component.css']
})
export class LeadsComponent implements OnInit {
  leads: any[] = [];
  filteredLeads: any[] = [];
  loading = false;
  errorMessage = '';

  // Search and filter
  searchTerm = '';
  selectedCategory = '';
  selectedStatus = '';
  categories: any[] = [];

  constructor(
    private apiService: ApiService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadLeads();
  }

  loadLeads() {
    this.loading = true;
    this.errorMessage = '';
    
    // Call the leads API endpoint
    this.apiService.getLeads().subscribe({
      next: (response: ApiResponse) => {
        // Extract data from standardized response
        const data = extractData(response);
        // Filter to only show "Yet to Quote" and "Expired" quotes
        const allLeads = Array.isArray(data) ? data : [];
        this.leads = allLeads.filter(lead => 
          lead.status === 'Yet to Quote' || lead.status === 'Expired'
        );
        this.filteredLeads = [...this.leads];
        this.extractCategories();
        this.loading = false;
      },
      error: (err: ApiResponse) => {
        console.error('Error loading leads:', err);
        const errorMessage = extractErrorMessage(err) || 'Failed to load leads';
        this.errorMessage = errorMessage;
        this.loading = false;
      }
    });
  }

  extractCategories() {
    const categorySet = new Set<string>();
    this.leads.forEach(lead => {
      if (lead.product?.category?.name) {
        categorySet.add(lead.product.category.name);
      }
    });
    this.categories = Array.from(categorySet).map(name => ({ name }));
  }

  onSearch() {
    this.applyFilters();
  }

  onCategoryChange() {
    this.applyFilters();
  }

  onStatusChange() {
    this.applyFilters();
  }

  applyFilters() {
    this.filteredLeads = this.leads.filter(lead => {
      // Search filter
      const matchesSearch = !this.searchTerm || 
        (lead.product?.productId?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
         lead.product?.name?.toLowerCase().includes(this.searchTerm.toLowerCase()));

      // Category filter
      const matchesCategory = !this.selectedCategory ||
        lead.product?.category?.name === this.selectedCategory;

      // Status filter
      const matchesStatus = !this.selectedStatus ||
        lead.status === this.selectedStatus;

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedCategory = '';
    this.selectedStatus = '';
    this.filteredLeads = [...this.leads];
  }

  formatDate(date: string | Date): string {
    if (!date) return '-';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Expired':
        return 'status-expired';
      case 'Yet to Quote':
        return 'status-pending';
      default:
        return 'status-default';
    }
  }

  getShippingAddress(lead: any): string {
    // Check both shippingAddress and shippingDetails (for backward compatibility)
    const shippingData = lead.product?.shippingAddress || lead.product?.shippingDetails;
    if (!shippingData) return '-';
    
    const parts = [
      shippingData.city,
      shippingData.state,
      shippingData.country || 'INDIA',
      shippingData.pincode ? `PIN: ${shippingData.pincode}` : ''
    ].filter(Boolean);
    
    return parts.join(', ') + '.';
  }

  getShippingTerm(lead: any): string {
    // Check both shippingTerm in product and shippingDetails
    const shippingTerm = lead.product?.shippingTerm || 
                        lead.product?.shippingDetails?.shippingTerm || 
                        lead.product?.shippingAddress?.shippingTerm;
    if (!shippingTerm) return '-';
    return shippingTerm.toUpperCase();
  }

  viewLeadDetails(lead: any) {
    this.router.navigate(['/leads/view', lead._id]);
  }
}
